<template>
  <div v-if="questions" class="h-full">
    <BaseTable
      :headers="questionHeaders"
      :list="localQuestions"
      :is-add-new-hidden="mode == 'view'"
      table-id="qst-questions"
      clickable
      hide-grouping
      :total-records="localQuestions.length"
      is-download-button-hidden
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="onRefreshData"
      @table-item-clicked="onQuestionClick"
      @add-new="onQuestionClick">
      <template #custom="{ item, header }">
        <div class="flex items-center justify-center">
          <div v-if="header === 'image'">
            <button
              v-if="item.image"
              type="button"
              class="rounded"
              @click.stop="onImageExpandClick(item.image)">
              <IconImage class="w-5 h-5 rounded text-theme-600 hover:bg-cyan-light" />
            </button>
            <span v-else> - </span>
          </div>
          <div v-else-if="header === 'allowNull'">
            <BaseTableCellStatus
              color="blue"
              :item="item.allowNull"
              text-true="Allow"
              text-false="Not Allowed"></BaseTableCellStatus>
          </div>
          <div v-else-if="header === 'isIncludeLastVisitData'">
            <BaseTableCellStatus
              color="blue"
              :item="item.isIncludeLastVisitData"
              text-true="Include"
              text-false="Not Included"></BaseTableCellStatus>
          </div>
        </div>
      </template>
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1" v-if="props.mode == 'edit'">
          <button
            v-if="item.isFromManager"
            type="button"
            class="p-1.5 text-red-400 rounded-full hover:bg-red-50"
            @click="onDeleteQuestion(item as QstQuestionDetails)">
            <IconTrashOutline class="size-4" />
          </button>
        </div>
      </template>
    </BaseTable>
    <InfoModal v-if="previewImage" @primary-button-clicked="previewImage = null">
      <template #content> <img :src="previewImage" /> </template>
    </InfoModal>

    <BaseModalConfirm
      v-if="deleteRowItem && modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingleQuestion">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text>
        {{ $t('messages.info.deleteSingle', { 'item': deleteRowItem.title }) }}
      </template>
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';

import type { TableFilterData } from '@/types/Base';
import { type QstQuestionDetails } from '@/types/Questionnaires';

import BaseTable from '../common/BaseTable.vue';
import { useToastStore } from '@/stores/toast';

import IconImage from '@/assets/icons/IconImage.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import InfoModal from '../common/InfoModal.vue';
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';
import BaseModalConfirm from '../common/BaseModalConfirm.vue';
import { filterData, sortData } from '@/utils/table';

import { removeQuestionsByIds } from '@/services/api/questionnaires';
import { deepCopy } from '@/utils/data';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();

const props = defineProps<{
  mode: 'view' | 'edit';
  questionnaireId: string;
  questions: QstQuestionDetails[];
  resetState: boolean;
}>();

const localQuestions = ref<QstQuestionDetails[]>([]);
onMounted(() => {
  localQuestions.value = deepCopy<QstQuestionDetails[]>(props.questions);
});

const emit = defineEmits(['delete', 'navigate-to-question']);

const questionHeaders = ref([
  { value: 'title', text: $t('questionnaire.title'), type: 'text', active: true },
  { value: 'description', text: $t('questionnaire.description'), type: 'text', active: true },
  { value: 'mwDataType.name', text: $t('question.dataType'), type: 'text', active: true },
  { value: 'sort', text: $t('question.sort'), type: 'text', active: true },
  { value: 'allowNull', text: $t('question.nullValues'), type: 'custom', active: true },
  {
    value: 'isIncludeLastVisitData',
    text: $t('question.lastVisitData'),
    type: 'custom',
    active: true,
  },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'status', active: true },
  {
    value: 'image',
    text: $t('question.image'),
    type: 'custom',
    active: true,
  },
  { value: 'actions', text: $t('baseObject.actions'), active: props.mode == 'edit' },
]);

const previewImage = ref<string | null>(null);

function onImageExpandClick(image: string) {
  previewImage.value = `data:image/jpeg;base64,${image}`;
}

watch(
  () => props.mode,
  (newMode) => {
    const actionsColumn = questionHeaders.value.find((header) => header.value === 'actions');
    if (actionsColumn) {
      actionsColumn.active = newMode === 'edit';
    }
  }
);

function onQuestionClick(item?: QstQuestionDetails) {
  emit('navigate-to-question', item ? item.id : undefined);
}

async function onRefreshData(data: TableFilterData) {
  localQuestions.value = props.questions;
  if (data && data.query)
    localQuestions.value = filterData(localQuestions.value, data.query, ['title', 'description']);
  if (data && data.column !== undefined && data.order)
    localQuestions.value = sortData(
      localQuestions.value,
      data.column,
      data.order,
      questionHeaders.value
    );
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<QstQuestionDetails>();

function onDeleteQuestion(item: QstQuestionDetails) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

async function onDeleteSingleQuestion() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeQuestionsByIds(props.questionnaireId, [idToDelete]);

  localQuestions.value = localQuestions.value.filter((qst) => qst.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
  emit('delete');
}

async function onDeleteMultiselect(items: QstQuestionDetails[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  const res = await removeQuestionsByIds(props.questionnaireId, idsToDelete);
  localQuestions.value = localQuestions.value.filter((qst) => !idsToDelete.includes(qst.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}
</script>
